<!--
  AB#22077 — one record's own audit entries (an order, a company, a user),
  newest first, each opening to its before and after. The Activity tab.
  Rendered only for holders of view-record-activity (or view-audit-log);
  the API refuses everybody else, so a failed read says so and nothing more.
-->
<template>
  <div class="ra" :data-test="`record-activity-${type}`">
    <p v-if="loading && !items.length" class="ra-mute" data-test="record-activity-loading">
      Loading…
    </p>
    <p v-else-if="failed" class="ra-err" data-test="record-activity-failed">
      The activity of this record could not be loaded.
    </p>
    <p v-else-if="!items.length" class="ra-mute" data-test="record-activity-empty">
      Nothing has been recorded for this {{ noun }} yet.
    </p>
    <template v-else>
      <p class="ra-count">{{ total }} {{ total === 1 ? 'entry' : 'entries' }}, newest first</p>
      <ol class="ra-list">
        <li v-for="e in items" :key="e.id" class="ra-item" :data-test="`record-activity-${e.id}`">
          <button
            type="button"
            class="ra-row"
            :aria-expanded="openId === e.id"
            @click="toggle(e.id)"
          >
            <span class="ra-when">{{ whenLabel(e.at) }}</span>
            <span class="ra-main">
              <span class="ra-sum">{{ e.summary }}</span>
              <span class="ra-by">
                {{ e.actor.name ?? (e.actor.type === 'system' ? 'System' : 'Unknown') }}
                <template v-if="e.impersonator">
                  · via {{ e.impersonator.name ?? `#${e.impersonator.id}` }}</template
                >
                <template v-if="e.changes_count">
                  · {{ e.changes_count }} field{{ e.changes_count === 1 ? '' : 's' }}</template
                >
              </span>
            </span>
            <AuditOutcomePills :entry="e" />
          </button>
          <div v-if="openId === e.id" class="ra-detail">
            <AuditChangesTable :changes="e.changes ?? []" />
            <div class="ra-meta">
              <span class="mono">{{ e.action }}</span>
              <span v-if="e.ip"> · from {{ e.ip }}</span>
              <span v-if="e.route"> · {{ e.method }} {{ e.route }}</span>
            </div>
          </div>
        </li>
      </ol>
      <div v-if="lastPage > 1" class="ra-pager">
        <button type="button" class="dash-btn" :disabled="page <= 1" @click="go(page - 1)">
          Newer
        </button>
        <span class="ra-mute">Page {{ page }} of {{ lastPage }}</span>
        <button type="button" class="dash-btn" :disabled="page >= lastPage" @click="go(page + 1)">
          Older
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

import AuditChangesTable from '@/components/Audit/AuditChangesTable.vue';
import AuditOutcomePills from '@/components/Audit/AuditOutcomePills.vue';
import { useRecordActivity, whenLabel } from '@/composables/useAuditLog';

const props = withDefaults(
  defineProps<{
    type: 'order' | 'company' | 'user' | string;
    id: number | string | null | undefined;
    noun?: string;
  }>(),
  { noun: 'record' }
);

const { items, total, page, lastPage, loading, failed, load } = useRecordActivity(props.type);
const openId = ref<number | null>(null);

const toggle = (id: number): void => {
  openId.value = openId.value === id ? null : id;
};
const go = (p: number): void => {
  openId.value = null;
  void load(props.id, { page: p });
};

onMounted(() => void load(props.id, { page: 1 }));
watch(
  () => props.id,
  () => {
    openId.value = null;
    void load(props.id, { page: 1 });
  }
);
</script>

<style scoped>
.ra {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  color: var(--ink);
}
.ra-mute,
.ra-count {
  margin: 0;
  color: var(--ink-mute);
  font-size: 12.5px;
}
.ra-err {
  margin: 0;
  color: var(--danger);
  font-size: 12.5px;
}
.ra-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}
.ra-item + .ra-item {
  border-top: 1px solid var(--border-soft);
}
.ra-row {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.ra-row:hover,
.ra-row[aria-expanded='true'] {
  background: var(--surface-2);
}
.ra-row:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}
.ra-when {
  color: var(--ink-soft);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 12.5px;
}
.ra-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ra-sum {
  overflow-wrap: anywhere;
}
.ra-by {
  color: var(--ink-mute);
  font-size: 11.5px;
}
.ra-detail {
  padding: 4px 12px 12px;
  background: var(--surface-2);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ra-meta {
  font-size: 11.5px;
  color: var(--ink-mute);
}
.mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.ra-pager {
  display: flex;
  align-items: center;
  gap: 10px;
}
@media (max-width: 640px) {
  .ra-row {
    grid-template-columns: 1fr;
  }
}
</style>
